// src/components/job-engine/useJobLineage.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { getJobLineage } from "@/api/jobEngine";
import type { JobLineage } from "@/api/jobEngine";

export interface UseJobLineageResult {
  data: JobLineage | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useJobLineage(jobId: string): UseJobLineageResult {
  const [data, setData] = useState<JobLineage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!jobId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    setError(null);
    getJobLineage(jobId, ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) setData(r); })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (isAxiosError(err)) setError(err.response?.data?.title ?? "Failed to load lineage");
        else setError("An unexpected error occurred");
      })
      .finally(() => { if (!ctrl.signal.aborted) setIsLoading(false); });
    return () => ctrl.abort();
  }, [jobId, tick]);

  return { data, isLoading, error, refetch };
}
